import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import marketEnergyConfig from './market-energy-config.json';
import { getChartConfig } from './market-energy-utils';
import './market-energy-allocation.css';

/**
 * Market Energy Allocation Chart Component
 * Displays risk-wise energy allocation across different markets
 */
const MarketEnergyAllocation = ({ 
  selectedRisk,
  data,
  isLoading = false,
  error = null,
  onRetry = null,
  className = "",
  onDataUpdate = null
}) => {
  // Use local configuration
  const config = marketEnergyConfig;

  // Generate chart option
  const chartOption = useMemo(() => {
    return getChartConfig(data, config, selectedRisk);
  }, [data, selectedRisk, config]);

  // Notify parent when data updates
  React.useEffect(() => {
    if (onDataUpdate && data) {
      onDataUpdate(data);
    }
  }, [data, onDataUpdate]);

  // Render loading state
  if (isLoading) {
    return (
      <div className="market-energy-loading">
        <div className="market-energy-loading-spinner">⟳</div>
        Loading market energy data...
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="market-energy-error">
        <div className="market-energy-error-message">
          {error.message || 'Failed to load market energy allocation data'}
        </div>
        {error.canRetry && onRetry && (
          <button 
            className="market-energy-retry-button" 
            onClick={onRetry}
            aria-label="Retry loading market energy data"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Render no data state
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="market-energy-error">
        <div className="market-energy-error-message">
          No market energy allocation data available
        </div>
      </div>
    );
  }

  return (
    <div className={`market-energy-chart-container ${className}`}>
      <div 
        className="market-energy-chart-wrapper"
        role="img"
        aria-label={config.accessibility?.chartDescription || "Market energy allocation chart"}
      >
        <ReactECharts
          option={chartOption}
          style={{ 
            height: config?.chart?.height || '300px', 
            width: '100%' 
          }}
          opts={{ renderer: 'canvas' }}
        />
      </div>
    </div>
  );
};

// PropTypes for development
MarketEnergyAllocation.propTypes = {
  selectedRisk: PropTypes.string.isRequired,
  data: PropTypes.object,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
  onRetry: PropTypes.func,
  className: PropTypes.string,
  onDataUpdate: PropTypes.func
};

// Default props
MarketEnergyAllocation.defaultProps = {
  data: null,
  isLoading: false,
  error: null,
  onRetry: null,
  className: "",
  onDataUpdate: null
};

export default React.memo(MarketEnergyAllocation);
